import { useEffect, useState } from 'react';
import { ListItem, Typography } from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import _ from 'lodash';
import { makeAutoObservable, toJS } from 'mobx';
import { Route, Switch } from 'react-router-dom';
import { GridRowParams } from '@material-ui/data-grid';
import ActionsMenu from '../../components/other/ActionsMenu';
import { FormInputTypes } from '../../utils/constants';
import { drawerStore } from '../../state/drawerStore';
import VMDataGrid from '../../components/main/VMDataGrid';
import { UserStatusBadge } from '../../components/other/CellBadges';
import UserDetails from './UserDetails';
import TextLink from '../../components/other/TextLink';
import { gridColumn } from '../../utils/gridUtils';
import { toastStore } from '../../state/toastStore';
import { Api } from '../../api';
import {
  route,
  USER_DETAILS_TABS,
  userDetailsTabParameters,
  userDetailsTabRoute,
} from '../../utils/routing';
import { PAGES } from '../../components/main/MainComponent';
import { ReactComponent as UserProfileIcon } from '../../images/Users/userProfileImage.svg';

const userRows = ['name', 'familyName', 'email', 'role', 'password'];

const userAddInputs = [
  { name: 'name', label: 'firstName', type: FormInputTypes.textField },
  { name: 'familyName', label: 'lastName', type: FormInputTypes.textField },
  { name: 'email', type: FormInputTypes.textField },
  {
    name: 'role',
    type: FormInputTypes.select,
    options: ['User', 'Admin'],
  },
  { name: 'password', type: FormInputTypes.password },
  { name: 'confirmPassword', type: FormInputTypes.password },
];

const filterParamOptions = [
  { label: 'First name', value: 'name' },
  { label: 'Last name', value: 'familyName' },
  { label: 'Email', value: 'email' },
];

const getUrlParams = (
  param?: string,
  filterValue?: string,
  paginationToken?: string,
  sortBy?: string,
  orderBy?: string,
) => {
  const urlParams: any = {};
  if (param && filterValue) {
    urlParams[param] = filterValue;
  }

  if (paginationToken) {
    urlParams.paginationToken = paginationToken;
  }

  if (sortBy) {
    urlParams.sortBy = sortBy;
  }

  if (orderBy) {
    urlParams.orderBy = orderBy;
  }
  return urlParams;
};

class UsersStore {
  loading = false;
  private _users: any[] = [];
  userCount = 0;
  paginationToken?: string = undefined;
  sortBy?: string = 'name';
  orderBy?: string = 'asc';
  filterParam?: string = undefined;
  filterValue?: string = undefined;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get users() {
    return toJS(this._users); // Need to convert if mobxData is array
  }

  set users(users) {
    this._users = users;
  }

  setUsersCount(count: number) {
    this.userCount = count;
  }

  setSort(sortBy: string, orderBy: string) {
    this.sortBy = sortBy;
    this.orderBy = orderBy;
  }

  setFilter(param: string, value: string) {
    this.filterParam = param;
    this.filterValue = value;
  }
}

const UsersPage = observer(() => {
  const [usersStore] = useState(() => new UsersStore());
  const sortableFields = ['name', 'email'];

  const loadUsers = (
    param = undefined,
    filterValue = undefined,
    clearData = false,
    sortBy = undefined,
    orderBy = undefined,
  ) => {
    if (usersStore.loading) {
      return;
    }

    if (!usersStore.paginationToken) {
      if (usersStore.users.length > 0) {
        if (!clearData) {
          return;
        }
      }
    }

    if (clearData) {
      usersStore.users = [];
    }

    const urlParams = getUrlParams(param, filterValue, usersStore.paginationToken, sortBy, orderBy);
    usersStore.loading = true;
    urlParams.paginationToken = usersStore.paginationToken;

    Api.user.listUsers(urlParams).then((resp) => {
      const newUsers = [...usersStore.users, ...(resp?.data?.data?.users || [])];
      usersStore.users = newUsers;
      usersStore.setUsersCount(resp?.data?.data?.count ?? usersStore.users.length);
      usersStore.paginationToken = resp?.data?.data?.paginationToken;
      usersStore.loading = false;

      const userIds = newUsers.map((user: { id: string }) => user.id);
      Api.charge.listSessionsForUsers(userIds).then((sessionResp) => {
        const sessions = sessionResp.data?.data || [];
        const sessionMap = sessions.reduce(
          (
            acc: Record<string, { sessionCount: number; latestSessionDate: string }>,
            session: any,
          ) => ({
            ...acc,
            [session.userId]: {
              sessionCount: session.sessionCount,
              latestSessionDate: new Date(session.latestSessionStartAt).toLocaleDateString('en-GB'),
            },
          }),
          {},
        );

        usersStore.users = usersStore.users.map((user: any) => ({
          ...user,
          sessionCount: sessionMap[user.id]?.sessionCount || 0,
          latestSessionDate: sessionMap[user.id]?.latestSessionDate || null,
        }));
      });
    });
  };

  useEffect(() => {
    if (!usersStore.loading && (!usersStore.users || usersStore.users.length === 0)) {
      loadUsers('', '', false);
    }
  }, []);

  useEffect(() => {
    loadUsers(
      usersStore.filterParam,
      usersStore.filterValue,
      true,
      usersStore.sortBy,
      usersStore.orderBy,
    );
  }, [usersStore.sortBy, usersStore.orderBy]);

  const filterCallback = (param: string, val: string) => {
    usersStore.setFilter(param, val);
    usersStore.paginationToken = undefined;
    loadUsers(param, val, true);
  };

  const confirmAddUser = (values) => {
    values = _.pick(values, userRows);
    if (values.role === 'Admin') {
      values.admin = true;
    }

    values.role = undefined;

    Api.user.createUser(values).then(() => {
      loadUsers();
    });
  };

  const onClickUserAdd = () => {
    drawerStore.openDrawer({
      label: 'Add user',
      inputs: userAddInputs,
      clickCallback: () => confirmAddUser(toJS(drawerStore.values)),
    });
  };

  const userActions = () => (
    <ActionsMenu>
      <ListItem button onClick={() => toastStore.showNotImplemented()}>
        <Typography style={{ width: '100%' }}>Deactivate</Typography>
      </ListItem>
      <ListItem button onClick={() => toastStore.showNotImplemented()}>
        <Typography style={{ width: '100%' }}>Send email</Typography>
      </ListItem>
    </ActionsMenu>
  );

  const userColumns = [
    gridColumn('', {
      width: 60,
      sortableFields,
      renderCell: () => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
          <UserProfileIcon style={{ marginBottom: '4px' }} />
        </div>
      ),
    }),
    gridColumn('name', {
      flex: 1,
      sortableFields,
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
          <TextLink
            label={`${params.row.name} ${params.row.familyName}`}
            to={userDetailsTabParameters(params.row.id, USER_DETAILS_TABS.OVERVIEW)}
            divStyle={{ marginBottom: '0.375rem' }}
          />
          <span
            style={{ color: 'gray', fontSize: '0.9rem', fontStyle: 'italic' }}
          >{`${params.row.email}`}</span>
        </div>
      ),
    }),
    gridColumn('type', {
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const lastLoginDate = params.row.lastLoggedIn
          ? new Date(params.row.lastLoggedIn).toLocaleDateString('en-GB')
          : '-';

        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
            <span style={{ marginBottom: '0.375rem' }}>
              {params.row.isAdmin ? 'Admin' : 'User'}
            </span>
            <span
              style={{ color: 'gray', fontSize: '0.9rem', fontStyle: 'italic' }}
            >{`last login: ${lastLoginDate}`}</span>
          </div>
        );
      },
    }),
    gridColumn('sessions', {
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
          <span style={{ marginBottom: '0.375rem' }}>
            {params.row.sessionCount ? `${params.row.sessionCount} sessions` : '-'}
          </span>
          {params.row.latestSessionDate && (
            <span
              style={{ color: 'gray', fontSize: '0.9rem', fontStyle: 'italic' }}
            >{`last on: ${params.row.latestSessionDate}`}</span>
          )}
        </div>
      ),
    }),
    gridColumn('status', {
      flex: 0.5,
      minWidth: 150,
      renderCell: (params) => <UserStatusBadge user={params.row} />,
    }),
    gridColumn('  ', {
      width: 60,
      renderCell: () => (
        <div style={{ width: '0px', margin: '0px', padding: '0px' }}>{userActions()}</div>
      ),
    }),
  ];

  const onBottomScroll = async () => {
    if (!usersStore.loading) {
      loadUsers();
    }
  };

  return (
    <VMDataGrid
      onBottomScroll={onBottomScroll}
      onClickAdd={onClickUserAdd}
      rows={usersStore.users}
      columns={userColumns}
      pageSize={60}
      filterParamOptions={filterParamOptions}
      filterCallback={filterCallback}
      count={usersStore.userCount}
      onSortTypeChange={usersStore.setSort}
      rowHeight={60}
    />
  );
});

const UsersPageWrapper = () => (
  <Switch>
    <Route path={userDetailsTabRoute()} component={UserDetails} />
    <Route path={route(PAGES.Users)} component={UsersPage} />
  </Switch>
);
export default UsersPageWrapper;
